import analytics from 'aria-viewer/analytics/utils/kinesisAnalytics'
import * as EVENTS from 'aria-viewer/analytics/constants/desktopInteractions'
import * as COMMON_EVENTS from 'aria-viewer/analytics/constants/'
import { KINESIS_TOKEN, STOP_ANALYTICS_INITIATED_BY } from 'aria-viewer/tour/constants/'
import { VideoPlayerActions } from 'aria-viewer/players/videoplayer'
import { PANORAMA_DRAG_END, PANORAMA_TAP } from 'aria-viewer/players/panoplayer/actions/panoramaActions'
import { PLAY_POSITION_MEDIA_VIDEO } from 'aria-viewer/players/panoplayer/actions/positionalMediaActions'
import { TourActions } from 'aria-viewer/tour'
import {
    STOP_FADER_DRAWER,
    VR_INSTRUCTION_DRAWER,
    STOP_LIST_SCROLL,
    STOP_LIST_ITEM_SELECTED,
} from 'aria-viewer/drawer/drawerMenuActions'
import {
    HOTSPOT_CLICKED,
    HOTSPOT_MODAL_SCROLLED,
    HOTSPOT_MOUSE_ENTERED,
    HOTSPOT_TYPES,
    HOTSPOT_MEDIA_VIEWED,
} from 'aria-viewer/interactive-element/actions/hotspotActions'
import { InteractiveElementActions } from 'aria-viewer/interactive-element'
import { SET_CONTEXT_MENU, SHARE_MENU } from 'aria-viewer/contextmenu/contextMenuActions'
import { AUDIO_STOP, AUDIO_PLAY, AUDIO_TOUR } from 'aria-viewer/audiomanager/audioManagerActions'
import { LANGUAGE_CHANGE } from 'aria-viewer/language/languageActions'
import {
    SHOW_PREVIEW,
    NEXT_MEDIA,
    INFO_ICON_CLICKED,
    VIDEO_VIEWED,
    VIDEO_FULL_SCREEN,
    PANO_LOADED,
    SHARE_CLICKED,
} from 'aria-viewer/players/supplementalmedia/supplementalMediaActions'
import { SET_MAP_DISPLAY, SET_MAP_ZOOM } from 'aria-viewer/map/mapActions'
import MapDisplayModes from 'aria-viewer/map/MapDisplayModes'
import {
    ANALYTICS_LOCATION_BUTTON_HOVERED,
    ANALYTICS_EXP_SELECTED,
    ANALYTICS_ACTION_BUTTON_CLICKED,
    ANALYTICS_REGISTRATION_SUBMITTED_SUCCESS,
    ANALYTICS_SET_STOP_INITIATED_BY,
    ANALYTICS_FIRST_STOP_VIEWED,
    ANALYTICS_REGISTRATION_EXTERNAL_LINK_CLICKED,
    ANALYTICS_SUPERCONTENT_STARTED,
} from 'aria-viewer/analytics/analyticActions'
import { DESCRIPTION_SCROLLED, HIDE as AVATAR_HIDDEN } from 'aria-viewer/guide/avatarActions'
import { CUSTOM_MEDIA_LABELS, MEDIA_MODULE_TYPES } from 'aria-viewer/tour/constants/'
import { trackInteractionEvent, onPreProcess } from './interactionHelpers'
import { combineAnalytics } from 'aria-viewer/core/middleware/analyticsMiddleware'

import desktopAnalytics from '../analytics/desktopAnalytics'

const interactionsMappers = {
    [VideoPlayerActions.SET_CURRENT_QUALITY]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_VIDEO_QUALITYCHANGE)(getState)
    },

    [VideoPlayerActions.PLAY_VIDEO]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_VIDEO_START)(getState)
    },

    [VideoPlayerActions.VIDEO_DRAG_END]: (getState, action) => {
        const state = getState()

        const altMainMedia = state.getIn(['tour', 'altMainMedia'])

        if (altMainMedia && altMainMedia.skipButtonTitle == CUSTOM_MEDIA_LABELS.BACK_TO_SCENE)
            return trackInteractionEvent(EVENTS.EXP_TRIGGER_VIDEO_PAN)(getState)
        else return trackInteractionEvent(EVENTS.EXP_VIDEO_PAN)(getState)
    },

    // This interaction needs to be properly tested after the video player in the dev environment
    // is fixed.
    [VideoPlayerActions.SET_VIDEO_VOLUME]: (getState, action) => {
        const state = getState()

        const videoVolume = state.getIn(['video', 'videoVolume'])

        // Muted
        if (videoVolume == 0) {
            return trackInteractionEvent(EVENTS.EXP_VIDEO_MUTE)(getState)
        } else if (videoVolume > 0) {
            // Unmuted
            return trackInteractionEvent(EVENTS.EXP_VIDEO_UNMUTE)(getState)
        }
    },

    [TourActions.SKIPPED_ALT_MAIN_MEDIA]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_INTROVIDEO_SKIPPED)(getState)
    },

    [VideoPlayerActions.SEEKED]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_VIDEO_SEEK)(getState)
    },

    [PANORAMA_DRAG_END]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_PANO_PAN)(getState)
    },

    [PANORAMA_TAP]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_PANO_TAP)(getState)
    },

    [PLAY_POSITION_MEDIA_VIDEO]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_POSMEDIA_STARTED)(getState)
    },

    [STOP_FADER_DRAWER]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_STOPLIST_HOVER)(getState)
    },

    [STOP_LIST_SCROLL]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_STOPLIST_SCROLL)(getState)
    },

    [STOP_LIST_ITEM_SELECTED]: (getState, action) => {
        const { stopid, title } = action

        const properties = {
            Selected_Stop_ID: stopid,
            Selected_Stop_Title: title,
        }

        return trackInteractionEvent(EVENTS.EXP_STOPLIST_SELECTION, properties)(getState)
    },

    [TourActions.STOP_CHANGED]: (getState, action) => {
        const isTourGuideAudioPlaying = getState().getIn(['audioManager', 'state', 'AUDIO_TOUR']) === AUDIO_PLAY

        return trackInteractionEvent(EVENTS.EXP_SCENE_ENTER, {
            IsTourGuideAudioPlaying: isTourGuideAudioPlaying,
        })(getState)
    },

    [ANALYTICS_SUPERCONTENT_STARTED]: (getState, action) => {
        const sceneChangedEvent = interactionsMappers[TourActions.STOP_CHANGED](getState, action)

        return [trackInteractionEvent(COMMON_EVENTS.EXPERIENCE_RESTARTED)(getState), sceneChangedEvent]
    },

    [TourActions.USER_IS_IDLE]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_SCENE_10SECONDS)(getState)
    },

    [HOTSPOT_CLICKED]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_HOTSPOT_TAPPED)(getState)
    },

    [HOTSPOT_MODAL_SCROLLED]: (getState, action) => {
        let eventToTrack
        switch (action.hotspotType) {
            case HOTSPOT_TYPES.tooltip:
                eventToTrack = EVENTS.EXP_HOTSPOT_TEXT_COMPLETED
                break
            case HOTSPOT_TYPES.photo:
                eventToTrack = EVENTS.EXP_HOTSPOT_IMAGEDESCRIPTION_COMPLETED
                break
            case HOTSPOT_TYPES.video:
            case HOTSPOT_TYPES.video360:
                eventToTrack = EVENTS.EXP_HOTSPOT_VIDEODESCRIPTION_COMPLETED
                break
            default:
                eventToTrack = null
        }
        return eventToTrack && trackInteractionEvent(eventToTrack)(getState)
    },

    [HOTSPOT_MEDIA_VIEWED]: (getState, action) => {
        let eventToTrack
        switch (action.hotspotType) {
            case HOTSPOT_TYPES.panorama:
                eventToTrack = EVENTS.EXP_HOTSPOT_PANO_VIEWED
                break
            case HOTSPOT_TYPES.photo:
                eventToTrack = EVENTS.EXP_HOTSPOT_IMAGE_VIEWED
                break
            case HOTSPOT_TYPES.video:
            case HOTSPOT_TYPES.video360:
                eventToTrack = EVENTS.EXP_HOTSPOT_VIDEO_VIEWED
                break
            default:
                eventToTrack = null
        }
        return eventToTrack && trackInteractionEvent(eventToTrack)(getState)
    },

    [HOTSPOT_MOUSE_ENTERED]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_HOTSPOT_HOVER)(getState)
    },

    [InteractiveElementActions.TRIGGER_CLICKED]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_TRIGGER_POPUP_ENTER_TAPPED)(getState)
    },

    [InteractiveElementActions.TRIGGER_MOUSE_ENTERED]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_TRIGGER_POPUP_HOVER)(getState)
    },

    [TourActions.NEXT_POINT]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_SCENE_NEXTARROW_TAPPED)(getState)
    },

    [TourActions.PREV_POINT]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_SCENE_BACKARROW_TAPPED)(getState)
    },

    [TourActions.SHOW_ALT_MEDIA]: (getState, action) => {
        const { skipButtonTitle } = action.media || {}

        if (skipButtonTitle == CUSTOM_MEDIA_LABELS.BACK_TO_SCENE)
            return trackInteractionEvent(EVENTS.EXP_TRIGGER_VIDEO_STARTED)(getState)
    },

    [AVATAR_HIDDEN]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_TOURGUIDE_OFF)(getState)
    },

    [LANGUAGE_CHANGE]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_TOURGUIDE_CHANGELANGUAGE)(getState)
    },

    [DESCRIPTION_SCROLLED]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_TOURGUIDE_INFO_COMPLETED)(getState)
    },

    [SHOW_PREVIEW]: (getState, action) => {
        if (action.visible) return trackInteractionEvent(EVENTS.EXP_SUPPMEDIA_HOVER)(getState)
    },

    [NEXT_MEDIA]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_SUPPMEDIA_TAPPED)(getState)
    },

    [SHARE_CLICKED]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_SUPPMEDIA_SHARED)(getState)
    },

    [VIDEO_VIEWED]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_SUPPMEDIA_VIDEO_VIEWED)(getState)
    },

    [INFO_ICON_CLICKED]: (getState, action) => {
        const { mediaType } = action

        let event

        switch (mediaType) {
            case MEDIA_MODULE_TYPES.VIDEOS:
                event = EVENTS.EXP_SUPPMEDIA_VIDEO_INFO_TAPPED
                break
            case MEDIA_MODULE_TYPES.PHOTOS:
                event = EVENTS.EXP_SUPPMEDIA_PHOTO_INFO_TAPPED
                break
            case MEDIA_MODULE_TYPES.PANOS:
                event = EVENTS.EXP_SUPPMEDIA_PANO_INFO_TAPPED
                break
            default:
                return
        }

        return trackInteractionEvent(event)(getState)
    },

    [VIDEO_FULL_SCREEN]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_SUPPMEDIA_VIDEO_EXPAND)(getState)
    },

    [PANO_LOADED]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_SUPPMEDIA_PANO_VIEWED)(getState)
    },

    [SET_MAP_DISPLAY]: (getState, action) => {
        if (action.mode == MapDisplayModes.COLLAPSED) return trackInteractionEvent(EVENTS.EXP_MAP_COLLAPSED)(getState)

        if (action.mode == MapDisplayModes.EXPANDED) return trackInteractionEvent(EVENTS.EXP_MAP_HOVER)(getState)
    },

    [SET_MAP_ZOOM]: (getState, action) => {
        const state = getState()
        const currentZoomLevel = state.getIn(['map', 'zoomBounds', 'level'])
        const {
            zoom: { level },
        } = action

        const zoomEvent = currentZoomLevel > level ? EVENTS.EXP_MAP_ZOOM_OUT : EVENTS.EXP_MAP_ZOOM_IN

        return trackInteractionEvent(zoomEvent)(getState)
    },

    [ANALYTICS_FIRST_STOP_VIEWED]: (getState, action) => {
        return trackInteractionEvent(ANALYTICS_FIRST_STOP_VIEWED)(getState)
    },
    [ANALYTICS_SET_STOP_INITIATED_BY]: (getState, action) => {
        const { initiatedBy } = action

        if (initiatedBy == STOP_ANALYTICS_INITIATED_BY.map)
            return trackInteractionEvent(EVENTS.EXP_MAP_STOP_TAPPED)(getState)
    },

    [VR_INSTRUCTION_DRAWER]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_VRBUTTON_TAPPED)(getState)
    },

    [SET_CONTEXT_MENU]: (getState, action) => {
        let eventToTrack
        const currentContextMenu = getState().getIn(['contextMenu', 'name'])
        switch (currentContextMenu) {
            case SHARE_MENU:
                eventToTrack = EVENTS.EXP_MENU_SHARE_TAPPED
                break
            default:
                eventToTrack = null
        }
        return eventToTrack && trackInteractionEvent(eventToTrack)(getState)
    },

    [ANALYTICS_LOCATION_BUTTON_HOVERED]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_LOCATIONSDROPDOWN_TAPPED)(getState)
    },

    [ANALYTICS_EXP_SELECTED]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_LOCATIONSDROPDOWN_LOCATIONTAPPED)(getState)
    },

    [ANALYTICS_ACTION_BUTTON_CLICKED]: (getState, action) => {
        const { name, color, actionName } = action

        const properties = {
            CTA_type: actionName,
            CTA_name: name,
            Color: color,
        }

        return trackInteractionEvent(EVENTS.EXP_CTABUTTON_TAPPED, properties)(getState)
    },

    [TourActions.IS_WINDOW_FULLSCREEN]: (getState, action) => {
        const { isWindowFullScreen } = action
        const event = isWindowFullScreen ? EVENTS.EXP_EXPERIENCE_EXPAND : EVENTS.EXP_EXPERIENCE_MINIMIZED

        return trackInteractionEvent(event)(getState)
    },

    [ANALYTICS_REGISTRATION_SUBMITTED_SUCCESS]: (getState, action) => {
        return trackInteractionEvent(EVENTS.EXP_REGISTRATIONSUBMITTED)(getState)
    },

    [ANALYTICS_REGISTRATION_EXTERNAL_LINK_CLICKED]: (getState, action) => {
        return trackInteractionEvent(action.eventName)(getState)
    },
}

const customMapper = {
    analytics: analytics,

    token: KINESIS_TOKEN,

    mapper: combineAnalytics(desktopAnalytics.mapper, interactionsMappers),

    onPreProcess: onPreProcess,
}

export default customMapper
